/** The moon's face on a 24 × 24 drawing, its middle at 12, 12, 10 across: its seas, dark, as one
 * sees them from the north — `[x, y, wide, high, turned]` — and its brightest craters. */
const SEAS: readonly (readonly [number, number, number, number, number])[] = [
  [8, 11.5, 2.6, 4.2, 15], // Oceanus Procellarum
  [9.6, 8.2, 2.4, 1.9, -10], // Imbrium
  [13.6, 8.6, 1.5, 1.4, 0], // Serenitatis
  [15.2, 11, 2, 1.6, 20], // Tranquillitatis
  [18.4, 9.6, 1.1, 0.9, 0], // Crisium
  [17.2, 13.4, 1.3, 1.6, 10], // Fecunditatis
  [10.6, 15, 1.9, 1.3, 0], // Nubium
  [12, 5.2, 4, 0.7, -5], // Frigoris
];
const CRATERS: readonly (readonly [number, number, number])[] = [
  [11, 18.6, 0.75], // Tycho
  [8.6, 10.4, 0.5], // Copernicus
  [5.8, 12.4, 0.4], // Kepler
  [14.8, 16.6, 0.45],
  [16.2, 6.6, 0.35],
];

/**
 * The part of the moon the sun does not light, by its phase from new (0) through full (0.5): on
 * the left while it waxes, where earthshine shows the face faintly; the line between light and
 * dark an ellipse as wide as the cosine of the phase's angle.
 */
export function moonDark(phase: number): string {
  const k = Math.cos(2 * Math.PI * phase);
  // The line between light and dark bows as the lit part does.
  const back = phase < 0.5 === k > 0 ? 0 : 1;
  return [
    'M12 2',
    `A10 10 0 0 ${phase >= 0.5 ? 1 : 0} 12 22`,
    `A${String(Math.abs(k) * 10)} 10 0 0 ${String(back)} 12 2Z`,
  ].join(' ');
}

/**
 * The moon as it is seen at its phase: its face with its seas and craters, the part the sun does
 * not light dark but for earthshine; `turn` degrees clockwise as it is seen where one is — a
 * crescent upright in Tallinn, lying like a boat in Bangkok, upside down in Sydney.
 */
export function MoonIcon({
  phase,
  turn,
  size = 20,
}: {
  phase: number;
  turn: number;
  size?: number;
}) {
  return (
    <svg
      className="moon-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <g transform={`rotate(${String(turn)} 12 12)`}>
        <circle cx="12" cy="12" r="10" className="moon-face" />
        {SEAS.map(([x, y, wide, high, turned]) => (
          <ellipse
            key={`${String(x)},${String(y)}`}
            cx={x}
            cy={y}
            rx={wide}
            ry={high}
            transform={`rotate(${String(turned)} ${String(x)} ${String(y)})`}
            className="moon-sea"
          />
        ))}
        {CRATERS.map(([x, y, r]) => (
          <circle
            key={`${String(x)},${String(y)}`}
            cx={x}
            cy={y}
            r={r}
            className="moon-crater"
          />
        ))}
        <circle cx="12" cy="12" r="10" className="moon-limb" />
        <path d={moonDark(phase)} className="moon-shade" />
      </g>
    </svg>
  );
}

/** The same moon as an element of its own, for a map's marker. */
export function moonElement(phase: number, size: number): SVGSVGElement {
  const make = <K extends keyof SVGElementTagNameMap>(tag: K, said: Record<string, string>) => {
    const one = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [name, value] of Object.entries(said)) one.setAttribute(name, value);
    return one;
  };
  const svg = make('svg', {
    class: 'moon-icon',
    width: String(size),
    height: String(size),
    viewBox: '0 0 24 24',
  });
  const group = make('g', {});
  group.append(
    make('circle', { cx: '12', cy: '12', r: '10', class: 'moon-face' }),
    ...SEAS.map(([x, y, wide, high, turned]) =>
      make('ellipse', {
        cx: String(x),
        cy: String(y),
        rx: String(wide),
        ry: String(high),
        transform: `rotate(${String(turned)} ${String(x)} ${String(y)})`,
        class: 'moon-sea',
      }),
    ),
    ...CRATERS.map(([x, y, r]) =>
      make('circle', { cx: String(x), cy: String(y), r: String(r), class: 'moon-crater' }),
    ),
    make('circle', { cx: '12', cy: '12', r: '10', class: 'moon-limb' }),
    make('path', { d: moonDark(phase), class: 'moon-shade' }),
  );
  svg.append(group);
  return svg;
}
